import { useCallback, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { native } from '@muzly/shared';
import type { NativeF0Frame } from '@muzly/shared';

import { CTA_BOTTOM_PAD } from '../../../layouts';
import { framesToClip, rangeFramesToClip } from '../capture';
import { useOnboarding } from '../context';
import { CaptureProbe, isProbeOn } from '../probe';
import {
  RECORD_TESTS,
  SEGMENT_PROMPTS,
  attemptWarning,
  describeAttempt,
  isLastSegment,
  isLastTest,
  nextPosition,
  segmentAt,
  takeSpanSec,
} from '../recordTests';
import { isRoomTooNoisy } from '../roomNoise';
import { SUSTAIN_TONE_URL } from '../sustainGuide';
import { useRecordMachine } from '../useRecordMachine';
import { AllDoneView } from './AllDoneView';
import { CloseDiagnosisButton } from './CloseDiagnosisButton';
import { CountdownOverlay } from './CountdownOverlay';
import { FullScreenNotice } from './FullScreenNotice';
import { HeadphoneAdvice } from './HeadphoneAdvice';
import { NoisyRoomView } from './NoisyRoomView';
import { RecordReviewView } from './RecordReviewView';
import { RecordTestView } from './RecordTestView';
import { SongReferenceAudio } from './SongReferenceAudio';
import { SongReferenceGate } from './SongReferenceGate';
import { StepDoneView } from './StepDoneView';
import { TestGuideCard } from './TestGuideCard';
import { useSongReference } from './useSongReference';

import type { CaptureSource, SegmentId } from '../capture';
import type { MelodyNote } from '../melody';
import type { RecordPosition } from '../recordTests';
import type { SongScore } from '../songScoring';
import type { RecordErrKind, RecordMachine } from '../useRecordMachine';
import type { BackingView } from './RecordTestView';

/**
 * 진단 녹음 화면 — 상태기계(useRecordMachine)가 고른 단계를 그린다.
 *
 * 6단계: 가이드 → 3-2-1 → 측정 → **확인**(방금 잰 값 + 재진단) → … → 완료 / 에러.
 * 확인 단계가 재시도를 **세그먼트 단위**로 만든다 — 마지막에 몰아서 판정하면 무효가 났을
 * 때 전부 다시 찍어야 한다(티켓 12). 자체 전이를 가지므로 StepShell 기본 CTA는 숨긴다.
 *
 * 캡처는 네이티브 PitchTracker 엔진 경유 — 마이크는 네이티브가 소유하고 JS로는 f0/rms만
 * 건너온다. 스펙트럼이 없어 리포트의 resonance·singer's formant 축은 비활성.
 */

const errorKey = (err: RecordErrKind, source: CaptureSource | null): string =>
  // 브라우저 개발/데모 경로는 마이크 대신 합성 멜로디를 분석한다. 그 멜로디는 지속음
  // 유효성(`isValidVoice`)을 통과하지 못해 **항상 여기서 막히는데**, 기본 문구는 사용자
  // 목소리를 탓한다. 다시 해도 같은 결과라 무엇이 벽인지 말해 주는 게 유일하게 정직하다.
  err === 'invalid' && source === 'mock'
    ? 'web.diagnosis.error.mockInvalid'
    : `web.diagnosis.error.${err}`;

interface AttemptReview {
  summary: string;
  warning: string | null;
}

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** 방금 끝난 시도의 요약 + 경고 — 다시 할지 판단할 근거. 노래는 songReviewOf가 맡는다. */
const reviewOf = (
  segment: Exclude<SegmentId, 'song'>,
  tries: NativeF0Frame[][],
  t: Translate,
): AttemptReview => {
  const frames = tries[tries.length - 1] ?? [];
  const clip =
    segment === 'sustain'
      ? framesToClip(frames)
      : rangeFramesToClip(frames, segment);
  const warning = attemptWarning(segment, clip);
  const summary = describeAttempt(segment, clip);
  return {
    summary: t(summary.key, summary.params),
    warning: warning && t(warning.key, warning.params),
  };
};

/** 더 크게 치우친 구간 한 줄 — 없으면 말하지 않는다(치우침 없음도 사실이지만 경고는 아니다). */
const tendencyNote = (score: SongScore, t: Translate): string | null => {
  const segments = [
    { id: 'high', seg: score.high },
    { id: 'low', seg: score.low },
  ]
    .filter(({ seg }) => seg.tendency != null && seg.tendency !== 'onPitch')
    .sort(
      (a, b) => Math.abs(b.seg.centDev ?? 0) - Math.abs(a.seg.centDev ?? 0),
    );
  const worst = segments[0];
  return worst
    ? t(`web.diagnosis.song.tendency.${worst.id}.${worst.seg.tendency}`)
    : null;
};

/**
 * 노래 시도의 요약 — **레퍼런스 대비 채점**이다(clip만 보는 describeAttempt로는 못 낸다).
 * 채점은 프레임을 쥔 상태기계가 하고(`useRecordMachine`), 여기는 그 값을 문장으로 옮긴다.
 *
 * 채점이 안 되면 **0%가 아니라 "측정 불가"**라고 말한다 — 소리를 안 냈는데 0점이 뜨면
 * 사용자는 자기가 틀렸다고 읽는다.
 */
const songReviewOf = (
  score: SongScore | undefined,
  t: Translate,
): AttemptReview => {
  if (!score) {
    // 채점이 안 된 것도 경고다 — 이 회차에서 이 진단은 아무것도 안 남겼다.
    return {
      summary: t('web.diagnosis.song.unmeasured'),
      warning: t('web.diagnosis.song.unmeasuredHint'),
    };
  }
  return {
    summary: t('web.diagnosis.song.result', {
      accuracy: Math.round(score.accuracy),
      sung: score.sungNotes,
      total: score.totalNotes,
    }),
    warning: tendencyNote(score, t),
  };
};

/**
 * 확인 다이얼로그가 뭐라고 말할지. 진단이 끝났으면 완료를 알리고, 같은 진단 안에 세그먼트가
 * 남았으면(저음 → 고음) 다음에 낼 소리를 미리 말한다 — 여기서 "완료"라고 하면 고음을 왜
 * 또 재는지 알 수 없다.
 */
const reviewCopy = (
  pos: RecordPosition,
  testTitle: string,
  t: Translate,
): { title: string; hint: string | null } => {
  if (!isLastSegment(pos)) {
    const next = nextPosition(pos) as RecordPosition;
    return { title: t(SEGMENT_PROMPTS[segmentAt(next)].upNextKey), hint: null };
  }
  return {
    title: t('web.diagnosis.review.testDone', { test: testTitle }),
    hint: t(
      isLastTest(pos)
        ? 'web.diagnosis.review.lastTestHint'
        : 'web.diagnosis.review.nextTestHint',
    ),
  };
};

interface ErrorAction {
  label: string;
  onClick: () => void;
}

/**
 * 실패 종류 → 복구 선택지. 되돌릴 수 없는 `restartAll` 은 **`invalid` 의 두 번째 버튼**
 * 에만 남는다. `invalid` 의 기본은 **지속음 다시**다 — 리포트를 무효로 만드는 것은 지속음
 * 뿐이라(`buildReport`), 예전 기본이던 "마지막(=노래)만 다시"는 몇 번을 눌러도 같은 에러로
 * 돌아왔다. 권한은 진행을 지울 이유가 전혀 없고(같은 세그먼트를 다시 열면 된다),
 * 설정에서 꺼 둔 경우는 앱 안에서 다시 물어도 시트가 안 뜨므로 설정으로 보낸다.
 */
export const errorActions = (
  m: RecordMachine,
  t: Translate,
): {
  title: string;
  message: string;
  note?: string;
  primary: ErrorAction;
  secondary?: ErrorAction;
} => {
  const message = m.err ? t(errorKey(m.err, m.source)) : '';
  const title = t(`web.diagnosis.error.title.${m.err ?? 'unsupported'}`);
  const keptNote = t('web.diagnosis.error.keptNote');
  switch (m.err) {
    case 'blocked':
      return {
        title,
        message,
        note: keptNote,
        primary: {
          label: t('web.diagnosis.error.openSettings'),
          onClick: () => {
            void native.invoke('openAppSettings').catch(() => undefined);
          },
        },
        secondary: { label: t('web.diagnosis.error.retry'), onClick: m.begin },
      };
    case 'denied':
      return {
        title,
        message,
        note: keptNote,
        primary: { label: t('web.diagnosis.error.retry'), onClick: m.begin },
      };
    case 'invalid':
      return {
        title,
        message,
        note: t('web.diagnosis.error.invalidNote'),
        primary: {
          label: t('web.diagnosis.error.retrySustain'),
          onClick: m.retrySustain,
        },
        secondary: {
          label: t('web.diagnosis.error.restartAll'),
          onClick: m.restartAll,
        },
      };
    // 여기 오는 건 `unsupported` 뿐이지만, 새 실패가 생겨도 **되돌릴 수 없는 쪽이
    // 기본값이 되지 않도록** 기본 자리는 다시 시도다. 처음부터 다시는 둘째 자리에 둔다.
    default:
      return {
        title,
        message,
        note: keptNote,
        primary: { label: t('web.diagnosis.error.retry'), onClick: m.begin },
        secondary: {
          label: t('web.diagnosis.error.restartAll'),
          onClick: m.restartAll,
        },
      };
  }
};

/**
 * 측정이 막힌 화면. **복구는 항상 가장 좁은 것이 기본값**이다 — 예전에는 어떤 실패든
 * 유일한 버튼이 `restartAll` 이라, 3번째 진단에서 권한이 막히면 1·2번째 측정이 같이
 * 지워졌다. 되돌릴 수 없는 선택지는 두 번째 자리로 내리고, 무엇이 남아 있는지 말한다.
 */
const ErrorScreen = ({
  title,
  message,
  note,
  primary,
  secondary,
}: {
  title: string;
  message: string;
  note?: string;
  primary: ErrorAction;
  secondary?: ErrorAction;
}) => (
  <FullScreenNotice
    title={title}
    banner={message}
    // 축하 그림은 안 된다 — 여기는 막힌 화면이다. 하지만 제목·캡슐·여백은 소음 화면과
    // 같아야 한다: 스테이크가 가장 높은 순간이 유일하게 맨 회색 글씨였다(2026-09-16).
    showMascot={false}
    footer={
      <div className="flex w-full flex-col gap-3">
        <button
          type="button"
          onClick={primary.onClick}
          className="h-[50px] w-full rounded-full bg-[var(--bk-900)] text-base font-bold tracking-[0.08px] text-white motion-safe:active:scale-95"
        >
          {primary.label}
        </button>
        {secondary && (
          <button
            type="button"
            onClick={secondary.onClick}
            className="h-[50px] w-full rounded-full border-2 border-[hsl(var(--field-border))] text-base font-bold tracking-[0.08px] text-[var(--bk-900)] motion-safe:active:scale-95"
          >
            {secondary.label}
          </button>
        )}
      </div>
    }
  >
    {/* 낭독은 캡슐이 아니라 여기서 한 번만 — 제목과 캡슐을 둘 다 alert 로 두면 같은
        말을 두 번 읽는다. */}
    <p className="sr-only" role="alert" aria-live="assertive">
      {`${title} ${message}`}
    </p>
    {note && (
      <p className="mt-4 text-center text-sm text-muted-foreground">{note}</p>
    )}
  </FullScreenNotice>
);

/** 측정 화면 + 그 위에 뜨는 것들(카운트다운·확인 다이얼로그). */
const MeasureScreen = ({
  m,
  guide,
  guideAudioDurationMs,
  backing,
  backingPlayError,
}: {
  m: RecordMachine;
  /** 노래 진단의 정답 멜로디 — 측정 화면이 곡선 아래에 깔아 준다. */
  guide?: MelodyNote[];
  /** 그 곡 음원의 길이(ms) — 축과 제한시간이 음원을 끝까지 덮게 한다. */
  guideAudioDurationMs?: number;
  /** 노래 진단의 반주 재생 위치 재료 — 측정 화면 상단 파형. */
  backing?: BackingView;
  /**
   * 반주 `play()` 가 거절당한 사유(`SongReferenceAudio`). 계측 오버레이에만 쓴다 —
   * 반주가 안 들린 테이크는 채점이 성립하지 않는데, 전에는 이 거절이 통째로 삼켜져
   * 화면 어디에도 안 남았다(2026-09-18 실기기).
   */
  backingPlayError?: string | null;
}) => {
  const { t } = useTranslation();
  // 스위치는 URL이 정하고 URL이 바뀌려면 새로 뜬다 — 프레임마다(~30fps) 저장소를 다시
  // 읽을 이유가 없다.
  const probeOn = useMemo(() => isProbeOn(), []);
  const inReview = m.phase === 'review';
  /**
   * 방이 시끄러워 측정이 성립하지 않는다(시안 `884:9858`). 측정 중에만 본다 —
   * 확인 화면까지 이 판정을 들고 가면 이미 잰 값을 놓고 방 이야기를 하게 된다.
   */
  const noisyRoom =
    m.phase === 'recording' &&
    !m.paused &&
    isRoomTooNoisy({
      floorLocked: m.live.floorLocked,
      voicedSec: m.live.voicedSec,
      elapsedSec: m.elapsedSec,
      // 노래 진단은 반주가 스피커로 나가므로 조용한 창이 없는 것이 정상이다.
      backingTrack: m.test.id === 'song',
    });

  // 화면 전환이지 덮개가 아니다 — 뒤에서 타이머와 그래프가 계속 돌면 "저 값은 살아
  // 있나"가 화면에 남는다. 다시 재는 것은 이번 세그먼트만이다(진단 전체가 아니라).
  if (noisyRoom) {
    return (
      <Closable>
        <NoisyRoomView onRetry={m.retrySegment} />
      </Closable>
    );
  }

  // 진단 하나를 마쳤으면 화면을 갈아탄다(시안 `884:9451`). 같은 진단 안의 세그먼트
  // 이동(저음 → 고음)은 아래의 작은 다이얼로그로 남는다 — 화면을 갈아탈 일이 아니다.
  if (inReview && isLastSegment(m.pos)) {
    const summary =
      m.segment === 'song'
        ? songReviewOf(m.songScore, t)
        : reviewOf(m.segment, m.tries, t);
    return (
      <Closable>
        <StepDoneView
          step={m.pos.testIndex + 1}
          testTitle={t(m.test.titleKey)}
          hint={t(
            // 지속음을 다시 재는 중이면 이 확인이 곧 마감이다(`redoingSustain`).
            isLastTest(m.pos) || m.redoingSustain
              ? 'web.diagnosis.review.lastTestHint'
              : 'web.diagnosis.review.nextTestHint',
          )}
          summary={summary.summary}
          warning={summary.warning}
          attemptCount={m.tries.length}
          onRetry={m.retrySegment}
          onNext={m.nextSegment}
          busy={m.finishing}
        />
      </Closable>
    );
  }

  return (
    <div className="relative flex flex-1 flex-col">
      {/* 계측 오버레이 — 더보기 › 개발자 옵션에서만 켠다(`probe/probeFlag.ts`). */}
      {probeOn && (
        <CaptureProbe
          source={m.source}
          live={m.live}
          backingPlayError={backingPlayError}
        />
      )}
      {/* 다이얼로그가 떠 있는 동안 뒤 화면은 포커스도 낭독도 받지 않는다 — 방금 누른
          완료 버튼이 가려진 채 포커스를 쥐고 있으면 키보드가 갇힌다. */}
      {/* `false`가 아니라 undefined를 넘긴다 — inert는 값이 아니라 **있고 없고**로 도는
          속성이라 `inert=""`가 남으면 녹음 내내 완료 버튼을 못 누른다. */}
      <div inert={inReview || undefined} className="flex flex-1 flex-col">
        <RecordTestView
          test={m.test}
          segment={m.segment}
          live={m.live}
          elapsedSec={m.elapsedSec}
          isRecording={m.phase === 'recording'}
          stepIndex={m.pos.testIndex}
          stepTotal={RECORD_TESTS.length}
          onFinish={m.stop}
          paused={m.paused}
          onPause={m.pause}
          onResume={m.resume}
          onRestart={m.retrySegment}
          guide={guide}
          guideAudioDurationMs={guideAudioDurationMs}
          backing={backing}
        />
      </div>

      {m.phase === 'countdown' && <CountdownOverlay count={m.count} />}
      {inReview && !isLastSegment(m.pos) && (
        <RecordReviewView
          {...reviewCopy(m.pos, t(m.test.titleKey), t)}
          {...(m.segment === 'song'
            ? songReviewOf(m.songScore, t)
            : reviewOf(m.segment, m.tries, t))}
          attemptCount={m.tries.length}
          onRetry={m.retrySegment}
          onConfirm={m.nextSegment}
        />
      )}
    </div>
  );
};

/**
 * 셸이 `bare`라 여백이 없다 — 측정 화면 **말고** 이 단계의 다른 화면들(가이드·음원 게이트·
 * 에러·완료)은 여백 안에 놓여야 하므로 여기서 감싼다. 측정 화면만 가장자리까지 찬다.
 */
const Padded = ({ children }: { children: ReactNode }) => (
  <div
    className="flex flex-1 flex-col px-5"
    style={{ paddingBottom: CTA_BOTTOM_PAD }}
  >
    {children}
  </div>
);

/**
 * 말만 하고 멈춰 선 화면들의 껍데기 — 소음(`884:9858`)·단계 완료(`884:9451`)·전체 완료·
 * 에러가 전부 여기로 들어온다. **진단 플로우는 늘 나갈 수 있어야 한다**: 시안의 그 화면들엔
 * 헤더 `×` 가 있는데 구현은 에러 화면에만 달려 있었고, `NoisyRoomView` 주석은 있다고
 * 단언하고 있었다(2026-09-16). 한 군데서 달아 네 화면이 같이 갖게 한다.
 */
const Closable = ({ children }: { children: ReactNode }) => (
  <Padded>
    <header className="flex items-center justify-end">
      <CloseDiagnosisButton />
    </header>
    {children}
  </Padded>
);

export const VoiceRecordStep = () => {
  const { t } = useTranslation();
  const { next } = useOnboarding();
  // 레퍼런스는 진단 화면에 들어서자마자 받는다 — 노래는 마지막 진단이라 앞의 두 진단
  // 동안 받아 두면 대기가 안 보이고, 카운트다운이 끝난 뒤 재생이 실패하는 일도 없다.
  // 멜로디는 상태기계로 내려간다: 채점은 프레임을 쥔 그쪽에서 돈다.
  const song = useSongReference();
  const m = useRecordMachine(
    song.reference?.melody,
    song.reference?.durationMs,
  );
  const isSong = m.test.id === 'song';
  // 가이드는 노래 진단에만 내려간다 — 다른 진단엔 따라 부를 정답이 없다. 안내 카드가
  // 약속하는 시간과 측정 화면의 축이 같은 값을 쓰려면 둘 다 이것을 봐야 한다.
  const guide = isSong ? song.reference?.melody : undefined;
  // 음원 길이는 차트와 **같이** 흘러야 한다 — 하나만 넘기면 테이크가 다시 차트 끝에서
  // 끊기고 그 뒤 반주 아웃트로가 잘린다.
  const guideAudioDurationMs = isSong ? song.reference?.durationMs : undefined;
  // 반주 `<audio>` 를 파형이 **읽는** 통로. 재생을 거는 것은 `SongReferenceAudio` 하나다.
  const backingAudioRef = useRef<HTMLAudioElement>(null);
  // 이어폰 권장은 노래 안내에서 한 번만 — 닫은 뒤 다시 찍기로 돌아와도 또 띄우지 않는다.
  const [adviceClosed, setAdviceClosed] = useState(false);
  // 반주 `play()` 가 거절당했거나 재생이 저절로 멈춘 사유. 렌더 흐름을 건드리지 않게 ref 에 담는다 — 오버레이가
  // 꺼져 있어도 모으고(프레임당 비용 없음), 켜져 있으면 `mr` 칸에 뜬다. 상태로 두면 실패
  // 한 번에 측정 화면이 리렌더돼 채점 앵커 근처를 흔든다.
  const backingPlayErrorRef = useRef<string | null>(null);
  const onBackingPlayFailed = useCallback((error: unknown) => {
    const name =
      error instanceof Error ? error.name || error.message : String(error);
    backingPlayErrorRef.current = name;
    // 오버레이가 꺼진 기기에서도 Safari 웹 인스펙터로는 보이게 한다.
    console.warn('[backing] playback problem:', name);
  }, []);
  if (m.phase === 'error') {
    return (
      <Closable>
        <ErrorScreen {...errorActions(m, t)} />
      </Closable>
    );
  }

  if (m.phase === 'done') {
    return (
      <Closable>
        <AllDoneView onResult={next} onRetry={m.restartAll} />
      </Closable>
    );
  }

  const guideCard = (
    <TestGuideCard
      test={m.test}
      index={m.pos.testIndex}
      total={RECORD_TESTS.length}
      onStart={m.begin}
      limitSec={takeSpanSec(m.test, guide, guideAudioDurationMs)}
      // 미리듣기 두 갈래. 노래는 **가이드 보컬 버전**을(부를 멜로디가 들려야 한다),
      // 지속음은 배포된 기준음 파일을 바로 건다 — 1.3초짜리라 미리 받아 둘 이유가 없고,
      // 못 받으면 재생만 조용히 실패한다(진단은 그대로 간다). 둘 다 녹음 전이라 채점의
      // 시간 앵커와 무관하다.
      {...(isSong && song.reference
        ? { previewUrl: song.reference.guideAudioObjectUrl }
        : m.test.id === 'sustain'
          ? { previewUrl: SUSTAIN_TONE_URL }
          : {})}
    />
  );

  if (m.phase === 'guide') {
    return (
      <Padded>
        {isSong ? (
          <SongReferenceGate
            status={song.status}
            onRetry={song.retry}
            onSkip={m.skipTest}
          >
            {guideCard}
            {m.speakerRoute && !adviceClosed && (
              <HeadphoneAdvice onClose={() => setAdviceClosed(true)} />
            )}
          </SongReferenceGate>
        ) : (
          guideCard
        )}
      </Padded>
    );
  }

  return (
    <>
      {/* 가이드는 노래 진단에만 내려간다 — 다른 진단엔 따라 부를 정답이 없다. */}
      <MeasureScreen
        m={m}
        guide={guide}
        guideAudioDurationMs={guideAudioDurationMs}
        backingPlayError={backingPlayErrorRef.current}
        backing={
          isSong && song.reference
            ? {
                peaks: song.reference.backingPeaks,
                // 음원 길이가 곧 반주 길이다. 없는 차트면 테이크 길이(차트 끝 + 여유)로.
                durationMs:
                  song.reference.durationMs ??
                  (takeSpanSec(m.test, guide) ?? 0) * 1000,
                audioRef: backingAudioRef,
              }
            : undefined
        }
      />
      {/* 재생은 녹음 단계에 들어서는 순간 건다. 게이트의 방 재기(`voice-gate.ts`
          CALIBRATION_MS)는 카운트다운 동안 이미 끝났다 — 세션을 카운트다운에서 여는 이유가
          그것이다(`useRecordMachine`). 반주가 그 창 안에서 울리면 잰 것이 방이 아니라
          반주라 바닥을 영영 못 잠갔고(`floor=OPEN`, `oct=114`), 잠글 때까지 미루면 첫
          프레임부터 부르는 사람에게는 반주가 중간에 나왔다(2026-09-16 실기기 둘 다).
          채점 앵커는 첫 프레임인데 프레임도 이 순간부터 모으므로(`markTake`) 따로 안 넘긴다.
          여기는 **반주**다: 가이드 보컬을 틀면 마이크가 그걸 같이 듣고 채점한다. */}
      {isSong && song.reference && (
        <SongReferenceAudio
          src={song.reference.audioObjectUrl}
          // 끊긴 테이크에서는 반주도 끊는다. `phase`는 `recording`에 머무르므로
          // (제한시간이 끊긴 테이크를 리뷰로 못 보낸다 — `useRecordMachine`) 이 조건이
          // 없으면 "녹음이 끊겼어요"를 띄운 채 음악만 끝까지 나간다. 다시 참이 되는
          // 유일한 길은 `retrySegment` → 카운트다운 → 녹음이라, 되감아 트는 것이 아니라
          // **새 테이크의 처음부터**다.
          playing={m.phase === 'recording' && !m.live.takeInterrupted}
          audioRef={backingAudioRef}
          onPlayFailed={onBackingPlayFailed}
        />
      )}
    </>
  );
};
